import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Globe, Users, Heart, Target, Sparkles, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';

const About = () => {
  const isIOSApp = useIsIOSApp();
  const values = [
    {
      icon: Globe,
      title: 'Global Community',
      description: 'We believe borders should never limit friendships. ExpatsSphere connects expats across every continent, creating a truly global family.'
    },
    {
      icon: Users,
      title: 'Inclusive Connections',
      description: 'Whether you just landed in a new city or have been abroad for years, everyone deserves a welcoming community to call home.'
    },
    {
      icon: Heart,
      title: 'Authentic Relationships',
      description: 'We foster genuine connections, not just networking. Our events and features are designed to help you build lasting friendships.'
    },
    {
      icon: Shield,
      title: 'Safe Environment',
      description: 'Your safety matters. We use AI moderation and community reporting to ensure ExpatsSphere remains a respectful, welcoming space.'
    }
  ];

  const stats = [
    { number: '1,247+', label: 'Active Members' },
    { number: '50+', label: 'Countries Represented' },
    { number: '100+', label: 'Cities Worldwide' },
    { number: '500+', label: 'Events Hosted' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-6 shadow-lg">
              <Globe className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              About <span className="text-primary">Expats</span><span className="italic text-primary">Sphere</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              We're on a mission to make every expat feel at home, anywhere in the world. 
              Because no one should navigate life abroad alone.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8 text-center">Our Story</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-6">
                ExpatsSphere was born from a simple observation: moving to a new country is exciting, 
                but it can also be isolating. The thrill of adventure often comes with the challenge 
                of building a new social circle from scratch.
              </p>
              <p className="mb-6">
                We've been there. We know what it's like to arrive in a new city without knowing a 
                single person. To miss important cultural events because you didn't know they existed. 
                To crave genuine connections but not know where to find them.
              </p>
              <p className="mb-6">
                That's why we created ExpatsSphere – a platform designed specifically for the global 
                community of expats, digital nomads, and international travelers. We're not just 
                another social network. We're a community built by expats, for expats.
              </p>
              <p>
                Today, ExpatsSphere connects thousands of members across the globe, helping them 
                discover events, make friends, and transform unfamiliar cities into places they 
                can truly call home.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary to-accent">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center text-white"
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-white/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Our Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These principles guide everything we do at ExpatsSphere
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 border-2 border-border"
              >
                <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mb-4">
                  <value.icon className="w-7 h-7 text-accent" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-12 h-12 text-accent mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Ready to Find Your People?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of expats who have already discovered their community on ExpatsSphere. 
              Your next adventure – and your next best friend – might be just one event away.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {!isIOSApp && (
              <Link to="/membership">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold">
                  Become a Member
                </Button>
              </Link>
              )}
              <Link to="/events">
                <Button variant="outline" className="h-12 px-8 rounded-full font-bold border-2">
                  Explore Events
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
